import {
  EAnalyticsLatencyBucket,
  EAnalyticsSizeBucket,
  type IAnalyticsPersonProperties,
  type IBuildAnalyticsPersonPropertiesProps,
} from "./product-analytics.types";

// NOTE: Bucket boundaries in characters. The product question is "are people
// writing one-liners or pasting documents", which buckets answer while a raw
// length plus a timestamp would help re-identify a specific message.
const MESSAGE_LENGTH_SMALL_MAX = 50;
const MESSAGE_LENGTH_MEDIUM_MAX = 200;
const MESSAGE_LENGTH_LARGE_MAX = 1_000;
const MESSAGE_LENGTH_HUGE_MAX = 5_000;

// NOTE: Bucket boundaries in BYTES. Deliberately separate from the message
// boundaries above: reusing those would put every file above 5 KB in "huge",
// which is every file anyone ever uploads. These are sized for documents and
// meeting recordings instead.
const FILE_SIZE_SMALL_MAX = 100 * 1024;
const FILE_SIZE_MEDIUM_MAX = 1024 * 1024;
const FILE_SIZE_LARGE_MAX = 25 * 1024 * 1024;
const FILE_SIZE_HUGE_MAX = 250 * 1024 * 1024;

// NOTE: Bucket boundaries in milliseconds, chosen to line up with how the
// latency actually feels rather than with round powers of ten.
const LATENCY_1S = 1_000;
const LATENCY_3S = 3_000;
const LATENCY_10S = 10_000;
const LATENCY_30S = 30_000;

// NOTE: Callers pass the length, never the text itself — the signature is the
// enforcement point for the tracking plan's "send the shape, not the content"
// rule, so a message body can't reach an event property by accident.
export const getMessageLengthBucket = ({
  length,
}: {
  length: number;
}): EAnalyticsSizeBucket => {
  if (length < MESSAGE_LENGTH_SMALL_MAX) return EAnalyticsSizeBucket.TINY;
  if (length < MESSAGE_LENGTH_MEDIUM_MAX) return EAnalyticsSizeBucket.SMALL;
  if (length < MESSAGE_LENGTH_LARGE_MAX) return EAnalyticsSizeBucket.MEDIUM;
  if (length < MESSAGE_LENGTH_HUGE_MAX) return EAnalyticsSizeBucket.LARGE;
  return EAnalyticsSizeBucket.HUGE;
};

// NOTE: Callers pass the byte count, never the file or its name — the
// signature is the enforcement point for the tracking plan's "send the shape,
// not the content" rule.
export const getFileSizeBucket = ({
  sizeBytes,
}: {
  sizeBytes: number;
}): EAnalyticsSizeBucket => {
  if (sizeBytes < FILE_SIZE_SMALL_MAX) return EAnalyticsSizeBucket.TINY;
  if (sizeBytes < FILE_SIZE_MEDIUM_MAX) return EAnalyticsSizeBucket.SMALL;
  if (sizeBytes < FILE_SIZE_LARGE_MAX) return EAnalyticsSizeBucket.MEDIUM;
  if (sizeBytes < FILE_SIZE_HUGE_MAX) return EAnalyticsSizeBucket.LARGE;
  return EAnalyticsSizeBucket.HUGE;
};

export const getLatencyBucket = ({
  durationMs,
}: {
  durationMs: number;
}): EAnalyticsLatencyBucket => {
  if (durationMs < LATENCY_1S) return EAnalyticsLatencyBucket.UNDER_1S;
  if (durationMs < LATENCY_3S) return EAnalyticsLatencyBucket.UNDER_3S;
  if (durationMs < LATENCY_10S) return EAnalyticsLatencyBucket.UNDER_10S;
  if (durationMs < LATENCY_30S) return EAnalyticsLatencyBucket.UNDER_30S;
  return EAnalyticsLatencyBucket.OVER_30S;
};

// NOTE: The B2B segmentation key. Everything interesting about a self-serve
// account ("how many people from the same company signed up separately") is a
// question about the domain, not the mailbox.
export const getEmailDomain = ({ email }: { email: string }): string => {
  const separatorIndex = email.lastIndexOf("@");
  if (separatorIndex === -1) return "";
  return email.slice(separatorIndex + 1).toLowerCase();
};

// NOTE: Single place where a user profile becomes PostHog person properties, so
// the PII decision recorded in the tracking plan is enforced in one file rather
// than re-litigated at every identify() call site.
export const buildAnalyticsPersonProperties = ({
  email,
  firstName,
  lastName,
  isSsoUser,
  providers,
  subscriptionPlan,
  createdAt,
}: IBuildAnalyticsPersonPropertiesProps): IAnalyticsPersonProperties => {
  return {
    email,
    name: `${firstName} ${lastName}`.trim(),
    email_domain: getEmailDomain({ email }),
    is_sso_user: isSsoUser,
    provider: providers.join(","),
    subscription_plan: subscriptionPlan,
    created_at: createdAt,
  };
};
